'use strict';

// external imports
import React, {
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Card} from 'primereact/card';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import {DataStructure} from '~/shared/src/utilities/dataStructure';

// frontend imports
import Graph, {
    ReferenceType as GraphReferenceType
} from '~/frontend/src/components/base/Graph';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import {
    MetaLink,
    MetaNode
} from '~/frontend/src/components/base/Graph';
import {
    ApplicationToYnItemTypeItemIdMapping,
    YnItemType,
    YnReferenceSet
} from '~/frontend/src/modules/panels/MetaModelDesignerView';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';


export type DefaultProps = {
    applicationToYnItemTypeItemIdMapping? : ApplicationToYnItemTypeItemIdMapping;
    schema? : Schema;
    ynItemTypes? : YnItemType[];
    ynReferenceSets? : YnReferenceSet[];

};
export const defaultProps : Required<DefaultProps> = {
    applicationToYnItemTypeItemIdMapping: {},
    schema                              : 'DATA',
    ynItemTypes                         : [],
    ynReferenceSets                     : []
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    dataContext : string;

    application? : string;

    highlightYnReferenceSetId? : number;
    onHighlightYnReferenceSetId? : ( id? : number ) => void;

    selectedYnReferenceSetId? : number;
    onSelectYnReferenceSetId? : ( id? : number ) => void;

    highlightYnItemTypeId? : number;
    onHighlightYnItemTypeId? : ( id? : number ) => void;

    selectedYnItemTypeId? : number;
    onSelectYnItemTypeId? : ( id? : number ) => void;
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ElusiveData = PanelElusiveDataState & {
    dataStructures? : DataStructure[];
    genericLinks? : MetaLink[];
    genericNodes? : MetaNode[];
    applicationToYnItemTypeIdMapping? : {
        [ name : string ] : number[];
    };
    itemTypeToYnItemTypeItemIdMapping? : {
        [ id : number ] : number;
    };
};

export type ReferenceType = {};


const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;


const MetaModelDesigner = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch = useDispatch(),
          {
              application,
              applicationToYnItemTypeItemIdMapping,
              dataContext,
              highlightYnItemTypeId,
              highlightYnReferenceSetId,
              onHighlightYnItemTypeId,
              onHighlightYnReferenceSetId,
              onSelectYnItemTypeId,
              onSelectYnReferenceSetId,
              schema,
              selectedYnItemTypeId,
              selectedYnReferenceSetId,
              uid,
              ynItemTypes,
              ynReferenceSets
          }                      = props,
          graphUid               = `${uid}_graph`,
          elusiveData            = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          graphRef               = useRef<GraphReferenceType>( null ),
          lastParams             = useRef<any>( {} ),
          filteredItemTypeIds    = useMemo(
              () => {
                  return application
                      ? Object.keys( applicationToYnItemTypeItemIdMapping[ application ] ).map( i => Number( i ) )
                      : undefined
              },
              [
                  application,
                  applicationToYnItemTypeItemIdMapping
              ]
          ),
          genericLinks           = useMemo(
              () => {
                  let retVal : MetaLink[] = elusiveData?.genericLinks ?? [];

                  if (
                      !elusiveData?.genericNodes
                      || application !== lastParams.current.application
                      || applicationToYnItemTypeItemIdMapping !== lastParams.current.applicationToYnItemTypeIdMapping
                      || genericLinks !== lastParams.current.genericLinks
                      || ynItemTypes !== lastParams.current.ynItemTypes
                      || ynReferenceSets !== lastParams.current.ynReferenceSets
                  ) {
                      const totalNum : { [ key : string ] : number } = {};

                      let newGenericLinks : MetaLink[] = [];

                      for ( const ynReferenceSet of ynReferenceSets ) {
                          if (
                              ynReferenceSet.targetId
                              && (
                                  !filteredItemTypeIds
                                  || (
                                      filteredItemTypeIds.includes( ynReferenceSet.sourceId )
                                      && filteredItemTypeIds.includes( ynReferenceSet.targetId )
                                  )
                              )
                          ) {
                              const classes : string[]   = [],
                                    referenceSetCountKey = Math.min( ynReferenceSet.sourceId, ynReferenceSet.targetId ) + '_' + Math.max( ynReferenceSet.sourceId, ynReferenceSet.targetId );

                              if ( !totalNum[ referenceSetCountKey ] ) {
                                  totalNum[ referenceSetCountKey ] = 0;
                              }

                              totalNum[ referenceSetCountKey ]++;

                              if ( ynReferenceSet.dirty ) {
                                  classes.push( 'yn-dirty' );
                              }

                              if ( ynReferenceSet.error?.count ) {
                                  classes.push( 'yn-error' );
                              }

                              if (
                                  ynReferenceSet.deleted
                                  || (
                                      application
                                      && (
                                          applicationToYnItemTypeItemIdMapping[ application ][ ynReferenceSet.sourceId ] === false
                                          || applicationToYnItemTypeItemIdMapping[ application ][ ynReferenceSet.targetId ] === false
                                      )
                                  )
                              ) {
                                  classes.push( 'yn-deleted' );
                              }

                              newGenericLinks.push( {
                                  id               : ynReferenceSet.id,
                                  svgId            : `link_${ynReferenceSet.id}`,
                                  label            : ynReferenceSet.label,
                                  fullLabel        : ynReferenceSet.fullLabel,
                                  cardinality      : ynReferenceSet.cardinality,
                                  source           : ynReferenceSet.sourceId,
                                  sourceId         : ynReferenceSet.sourceId,
                                  sourceCardinality: ynReferenceSet.cardinality === '1:1' || ynReferenceSet.cardinality === '1:n'
                                      ? '1'
                                      : 'N',
                                  target           : ynReferenceSet.targetId,
                                  targetId         : ynReferenceSet.targetId,
                                  targetCardinality: ynReferenceSet.cardinality === '1:1' || ynReferenceSet.cardinality === 'n:1'
                                      ? '1'
                                      : ynReferenceSet.cardinality === '1:n'
                                          ? 'N'
                                          : 'M',
                                  num              : totalNum[ referenceSetCountKey ],
                                  totalNum         : 1,
                                  reverse          : false,
                                  classes,
                                  disabled         : classes.includes( 'yn-deleted' )
                              } );
                          }
                      }

                      newGenericLinks = newGenericLinks.map(
                              ( l ) => {
                                  const referenceSetCountKey = Math.min( l.sourceId, l.targetId ) + '_' + Math.max( l.sourceId, l.targetId );

                                  if ( totalNum[ referenceSetCountKey ] ) {
                                      l.totalNum = totalNum[ referenceSetCountKey ];
                                  }

                                  return l;
                              }
                          )
                          .sort( ( a, b ) => a.label.localeCompare( b.label ) );

                      if ( JSON.stringify( newGenericLinks ?? {} ) !== JSON.stringify( retVal ?? {} ) ) {
                          retVal = newGenericLinks;

                          dispatch( setElusiveData( uid, { genericLinks: retVal } ) );
                      }
                  }

                  return retVal;
              },
              [
                  application,
                  applicationToYnItemTypeItemIdMapping,
                  ynItemTypes,
                  ynReferenceSets
              ]
          ),
          genericNodes           = useMemo(
              () => {
                  let retVal : MetaNode[] = elusiveData?.genericNodes ?? [];

                  if (
                      !elusiveData?.genericNodes
                      || application !== lastParams.current.application
                      || applicationToYnItemTypeItemIdMapping !== lastParams.current.applicationToYnItemTypeIdMapping
                      || genericLinks !== lastParams.current.genericLinks
                      || ynItemTypes !== lastParams.current.ynItemTypes
                  ) {
                      let newGenericNodes : MetaNode[] = [];

                      for ( const ynItemType of ynItemTypes ) {
                          if ( !filteredItemTypeIds || filteredItemTypeIds.includes( ynItemType.id ) ) {
                              const classes : string[] = [];

                              if ( ynItemType.dirty ) {
                                  classes.push( 'yn-dirty' );
                              }

                              if ( ynItemType.error?.count ) {
                                  classes.push( 'yn-error' );
                              }

                              if (
                                  ynItemType.deleted
                                  || (
                                      application
                                      && applicationToYnItemTypeItemIdMapping[ application ][ ynItemType.id ] === false
                                  )
                              ) {
                                  classes.push( 'yn-deleted' );
                              }

                              newGenericNodes.push( {
                                  id             : ynItemType.id,
                                  svgId          : `node_${ynItemType.id}`,
                                  label          : ynItemType.label,
                                  icon           : ynItemType.icon,
                                  x              : 0,
                                  y              : 0,
                                  width          : 0,
                                  height         : 0,
                                  itemTypeId     : ynItemType.id,
                                  fixPositions   : false,
                                  linkCount      : genericLinks.filter( l => l.sourceId === ynItemType.id || l.targetId === ynItemType.id ).length,
                                  uniqueLinkCount: genericLinks.reduce(
                                      ( acc, l ) => {
                                          if ( l.sourceId === ynItemType.id && !acc.includes( l.targetId ) ) {
                                              acc.push( l.targetId );
                                          }
                                          else if ( l.targetId === ynItemType.id && !acc.includes( l.sourceId ) ) {
                                              acc.push( l.sourceId );
                                          }

                                          return acc;
                                      },
                                      [] as number[]
                                  ).length,
                                  classes,
                                  disabled       : classes.includes( 'yn-deleted' )
                              } );
                          }
                      }

                      newGenericNodes = newGenericNodes.sort( ( a, b ) => a.label.localeCompare( b.label ) );

                      if ( JSON.stringify( newGenericNodes ?? {} ) !== JSON.stringify( retVal ?? {} ) ) {
                          retVal = newGenericNodes;

                          dispatch( setElusiveData( uid, { genericNodes: retVal } ) );

                          if ( application !== lastParams.current.application ) {
                              graphRef.current?.resetView();
                          }
                      }
                  }

                  return retVal;
              },
              [
                  application,
                  applicationToYnItemTypeItemIdMapping,
                  genericLinks,
                  ynItemTypes
              ]
          ),
          onSelectLink           = useCallback(
              ( ids : number[] ) => {
                  onSelectYnReferenceSetId?.( ids[ 0 ] );
                  onSelectYnItemTypeId?.( undefined );
                  return false;
              },
              [
                  onSelectYnItemTypeId,
                  onSelectYnReferenceSetId
              ]
          ),
          onHighlightLink        = useCallback(
              ( id ? : number ) => {
                  onHighlightYnReferenceSetId?.( id );
              },
              [
                  onHighlightYnReferenceSetId
              ]
          ),
          onSelectNode           = useCallback(
              ( ids : number[] ) => {
                  onSelectYnItemTypeId?.( ids[ 0 ] );
                  onSelectYnReferenceSetId?.( undefined );
                  return false;
              },
              [
                  onSelectYnItemTypeId,
                  onSelectYnReferenceSetId
              ]
          ),
          onHighlightNode        = useCallback(
              ( id ? : number ) => {
                  onHighlightYnItemTypeId?.( id );
              },
              [
                  onHighlightYnItemTypeId
              ]
          ),
          graph                  = useMemo(
              () => {
                  return <Graph
                      ref={graphRef}

                      uid={`${uid}_graph`}

                      dataContext={dataContext}

                      isGenericGraph={true}
                      genericLinks={genericLinks}
                      genericNodes={genericNodes}

                      labelPlaceholder={__( 'YN.base.frontend.metaModelDesigner.nodeLabel.placeholder' )}
                      layout="auto"
                      viewKey="designer"
                      showFilterMenu={false}
                      showOptionsMenu={false}
                      showStatistics={false}

                      showLinkCardinality={true}
                      showLinkNames={true}

                      nodeLabel="YN.base.backend.graph.applicationMetaModel.node.label"
                      nodesLabel="YN.base.backend.graph.applicationMetaModel.nodes.label"
                      linkLabel="YN.base.backend.graph.applicationMetaModel.link.label"
                      linksLabel="YN.base.backend.graph.applicationMetaModel.links.label"

                      linkSelectionMode="single"
                      selectedLinks={selectedYnReferenceSetId ? [ selectedYnReferenceSetId ] : []}
                      onSelectLink={onSelectLink}
                      onLinkOver={onHighlightLink}
                      onLinkOut={onHighlightLink.bind( null, undefined )}

                      nodeSelectionMode="single"
                      selectedNodes={selectedYnItemTypeId ? [ selectedYnItemTypeId ] : []}
                      onSelectNode={onSelectNode}
                      onNodeOver={onHighlightNode}
                      onNodeOut={onHighlightNode.bind( null, undefined )}
                  />;
              },
              [
                  genericLinks,
                  genericNodes,
                  onHighlightLink,
                  onHighlightNode,
                  onSelectLink,
                  onSelectNode,
                  selectedYnItemTypeId,
                  selectedYnReferenceSetId,
                  uid
              ]
          ),
          content                = useMemo(
              () => {
                  return <div
                      className="yn-form-card yn-view-card yn-view-card-standalone yn-view-card-standalone-view"
                      style={{ borderColor: 'transparent' }}
                  >
                      <Card
                          style={{ flex: '1' }}
                      >
                          <div
                              className="yn-view-wrapper yn-view-wrapper-graph yn-view-wrapper-card"
                          >
                              <div
                                  className=" yn-view-wrapper-border"
                              >
                                  <LoadingMaskPanel
                                      forComponent={graphUid}
                                      className=" yn-view-loadingmask yn-graphview-loadingmask"
                                      style={{ width: '100%' }}
                                  >
                                      {graph}
                                  </LoadingMaskPanel>
                              </div>
                          </div>
                      </Card>
                  </div>;
              },
              [
                  graph,
                  graphUid
              ]
          );

    lastParams.current = {
        application,
        applicationToYnItemTypeItemIdMapping,
        dataContext,
        genericLinks,
        schema,
        ynItemTypes,
        ynReferenceSets
    };

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {} )
    );

    // reset selectedYnItemTypeId if node not exists
    useLayoutEffect(
        () => {
            if ( !genericNodes.find( n => n.id === selectedYnItemTypeId ) ) {
                onSelectYnItemTypeId?.( undefined );
                graphRef.current?.resetSelectedNodes();
            }
        },
        [
            genericNodes
        ]
    );

    // highlight and select nodes
    useLayoutEffect(
        () => {
            graphRef.current?.highlightLinks(
                highlightYnReferenceSetId
                    ? [ highlightYnReferenceSetId ]
                    : []
            );
            graphRef.current?.selectLinks(
                selectedYnReferenceSetId
                    ? [ selectedYnReferenceSetId ]
                    : []
            );
            graphRef.current?.highlightNodes(
                highlightYnItemTypeId
                    ? [ highlightYnItemTypeId ]
                    : []
            );
            graphRef.current?.selectNodes(
                selectedYnItemTypeId
                    ? [ selectedYnItemTypeId ]
                    : []
            );
        }
    );

    return content;
} );
MetaModelDesigner.defaultProps = defaultProps;
MetaModelDesigner.displayName = 'MetaModelDesigner';

export default MetaModelDesigner;
